import errorReporter from '@app/errorReporter';
import {
  addListingToTour,
  getVirtualToursByUser,
} from 'api/VirtualTourController';
import { message } from 'components';
import { AuthContext, BuyerAgreementContext } from 'contexts';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import AppointmentBookingInternalContext from '../AppointmentBookingInternalContext';
import { getBookForOptions } from './helpers';
import type { BasicContactJson, FinalTour, Invitee } from './types';

type VirtualBookForType = 'self' | 'others';

type VirtualTabContextType = {
  bookForOptions: VirtualBookForType[];
  bookFor: VirtualBookForType;
  setBookFor: React.Dispatch<React.SetStateAction<VirtualBookForType>>;
  coop: BasicContactJson;
  setCoop: React.Dispatch<React.SetStateAction<BasicContactJson>>;
  tours: FinalTour[];
  setTours: React.Dispatch<React.SetStateAction<FinalTour[]>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchVirtualTours: () => void;
  invitees: Invitee[];
  setInvitees: React.Dispatch<React.SetStateAction<Invitee[]>>;
  selectedTourId: string;
  setSelectedTourId: React.Dispatch<React.SetStateAction<string>>;
};

const VirtualTabContext = React.createContext<VirtualTabContextType>(
  {} as VirtualTabContextType,
);

export const VirtualTabContextProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const { auth } = useContext(AuthContext);
  const { hasBuyerAgreed, showBuyerAgreementModal } = useContext(
    BuyerAgreementContext,
  );
  const {
    listing,
    authLastPhoneNumber,
    requiresBuyerAgreement,
    setActionsButtonData,
    setConditionalBuyerAgreementData,
  } = useContext(AppointmentBookingInternalContext);

  const [coop, setCoop] = useState<BasicContactJson>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tours, setTours] = useState<FinalTour[]>([]);
  const [selectedTourId, setSelectedTourId] = useState<string>('');
  const [invitees, setInvitees] = useState(
    new Array<Invitee>(3).fill({ email: '', name: '', invalidEmail: false }),
  );

  const bookForOptions = useMemo(
    () => getBookForOptions({ auth, listing: listing! }),
    [auth, listing],
  );
  const [bookFor, setBookFor] = useState<VirtualBookForType>(bookForOptions[0]);

  const addPropertyToTour = useCallback(
    async ({ buyerAgreementOveridden }: { buyerAgreementOveridden?: true }) => {
      if (!selectedTourId) return;

      if (
        !hasBuyerAgreed &&
        !buyerAgreementOveridden &&
        requiresBuyerAgreement &&
        showBuyerAgreementModal
      ) {
        showBuyerAgreementModal({
          onCancel: () => setIsSubmitting(false),
          onOk: () => addPropertyToTour({ buyerAgreementOveridden: true }),
        });
        return;
      }

      try {
        setIsSubmitting(true);
        await addListingToTour({
          virtualTourId: selectedTourId,
          listingId: listing!.id,
          invitees: invitees
            .filter(
              ({ name, email, invalidEmail }) =>
                !invalidEmail && Boolean(email) && Boolean(name),
            )
            .map(({ invalidEmail, ...invitee }) => invitee),
        });
        message.success('Property has been successfully added to virtual tour');
        history.push(`/listing/${listing!.id}/tour/${selectedTourId}/details`);
      } catch (err) {
        errorReporter.error(`Error adding property to virtual tour: ${err}`);
        setIsSubmitting(false);
      }
    },
    [
      hasBuyerAgreed,
      history,
      invitees,
      listing,
      requiresBuyerAgreement,
      selectedTourId,
      showBuyerAgreementModal,
    ],
  );

  useEffect(() => {
    setActionsButtonData([
      {
        variant: 'virtualTour',
        disabled: isSubmitting || !selectedTourId,
        onClick: () => addPropertyToTour({}),
      },
    ]);
  }, [addPropertyToTour, isSubmitting, selectedTourId, setActionsButtonData]);

  useEffect(() => {
    if (bookFor === 'self') {
      const authAddress = auth.org.addresses[+auth.location][+auth.address];
      setCoop({
        id: auth.id,
        name: `${auth.firstName} ${auth.lastName}`,
        firstName: auth.firstName,
        lastName: auth.lastName,
        email: auth.email,
        phone: authLastPhoneNumber?.number || auth.phone || '',
        org: {
          address: authAddress?.address || '',
          name: auth.org.name,
          phone: authAddress?.phone || '',
        },
      });
    } else {
      setCoop(null);
    }
  }, [auth, authLastPhoneNumber, bookFor]);

  const fetchVirtualTours = async () => {
    if (!coop) return;
    const coopId = coop.id || coop.uid;
    setIsLoading(true);
    try {
      const { virtualTours = [], tourListings = [] } =
        await getVirtualToursByUser(coopId);

      const listingMap: Record<string, string[]> = {};
      tourListings.forEach(tl => {
        listingMap[tl.virtualTour] ??= [];
        listingMap[tl.virtualTour].push(tl.listing);
      });

      const filteredTours = virtualTours.filter(virtualTour => !listingMap[virtualTour.id]?.includes(listing!._id));

      const finalTours: FinalTour[] = filteredTours
        .sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        )
        .map(virtualTour => ({
          ...virtualTour,
          key: virtualTour._id,
          startTime: moment(virtualTour.startTime).format('MMM Do, h:mm a'),
          listingsCount: listingMap[virtualTour.id]
            ? listingMap[virtualTour.id].length
            : 0,
        }));

      setTours(finalTours);
    } catch (err) {
      errorReporter.error(`Error fetching tours for user: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVirtualTours();
    setConditionalBuyerAgreementData({
      coopSelected: Boolean(coop),
      isBuyerShowing: true,
      isRecordShowing: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coop]);

  return (
    <VirtualTabContext.Provider
      value={{
        bookForOptions,
        bookFor,
        setBookFor,
        coop,
        setCoop,
        tours,
        setTours,
        isLoading,
        setIsLoading,
        fetchVirtualTours,
        invitees,
        setInvitees,
        selectedTourId,
        setSelectedTourId,
      }}
    >
      {children}
    </VirtualTabContext.Provider>
  );
};
export default VirtualTabContext;
